/**
 * Frontend permission helpers — unnati-compatible numeric permission IDs.
 *
 * User permissions come from GET /api/me/permissions/ as:
 *   { status: true, message: "Permission fetched successfully.", data: [8, 69, 70, ...] }
 *
 * List APIs attach action_permissions with the same numeric IDs per bucket:
 *   { view: [69], add: [70], update: [71], delete: [72], special: [73], export: [74] }
 */

export {
  type PermissionId,
  type ActionPermissionKey,
  type ActionPermissions,
  hasPermissionId,
  hasAnyPermissionId,
  hasActionPermission,
  hasAnyRowAction,
  extractActionPermissions,
} from '@/lib/permissions'

export { usePermissionIds, useModulePermissions } from '@/hooks/useModulePermissions'
export { usePermissionStore } from '@/stores/permissionStore'

/**
 * Per-control codenames — mirror the backend ``permission_map`` of the view each
 * control calls (see setu_backend utils/permission_sets.py). Check them with
 * ``usePermissionIds().hasCodename(...)``; company admins always pass.
 */
export const CUSTOMER_PERMISSIONS = {
  /** CustomerViewSet list/retrieve. */
  view: 'view_customers',
  /** CustomerViewSet create. */
  add: 'add_customers',
  /** CustomerViewSet update (profile, is_active, attributes), attribute-schema
   *  create, inbox contact PATCH and tag add/remove. */
  edit: 'edit_customers',
  /** CustomerViewSet destroy. */
  delete: 'delete_customers',
  /** CustomerViewSet bulk_import + import/export upload. */
  import: 'import_customers',
  /** CustomerViewSet export + import/export export. */
  export: 'export_customers',
} as const

export const INBOX_PERMISSIONS = {
  /** The /inbox route and its sidebar entry. */
  view: 'view_inbox',
  /** WhatsApp calls — crm/views/inbox_calls.py; registry row "WhatsApp Calls". */
  call: 'make_calls',
} as const

/** Setu Media library — mirrors crm/views/media_library.py permission_map. */
export const MEDIA_LIBRARY_PERMISSIONS = {
  /** The /setu-media route, its sidebar entry, list/download, the inbox
   *  "From Setu Media" picker and sending a library file. */
  view: 'view_media_library',
  /** Upload files. */
  add: 'add_media_library',
  /** Rename files. */
  change: 'change_media_library',
  /** Delete files (single + bulk). */
  delete: 'delete_media_library',
} as const
